"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importDefault(require("react"));
const MediaWidget_module_scss_1 = __importDefault(require("./MediaWidget.module.scss"));
const react_beautiful_dnd_1 = require("react-beautiful-dnd");
const ItemContainer_1 = __importDefault(require("./ItemContainer/ItemContainer"));
const feed_factory_helpers_1 = require("@feed-factory/feed-factory.helpers");
const feed_factory_media_preview_1 = __importDefault(require("@feed-factory/feed-factory.media-preview"));
const react_i18next_1 = require("react-i18next");
const AddMediaPane_1 = __importDefault(require("./AddMediaModal/AddMediaPane"));
const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
};
const getListStyle = (isDraggingOver) => ({
    background: isDraggingOver ? "lightblue" : "lightgrey",
});
const getItemStyle = (isDragging, draggableStyle) => (Object.assign({ 
    // some basic styles to make the items look a bit nicer
    userSelect: "none", 
    // change background colour if dragging
    background: isDragging ? "lightgreen" : "grey", position: "static" }, draggableStyle));
// Source: https://stackoverflow.com/a/8260383
const youtubeParser = (url) => {
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[7].length === 11 ? match[7] : null;
};
// Source: https://stackoverflow.com/a/11660798
const vimeoParser = (url) => {
    const regExp = /^.*(vimeo\.com\/)((channels\/[A-z]+\/)|(groups\/[A-z]+\/videos\/))?([0-9]+)/;
    const parseUrl = regExp.exec(url);
    return parseUrl ? parseUrl[5] : null;
};
const imageParser = (url) => {
    const subStrings = url.split("/");
    return subStrings[subStrings.length - 1];
};
const MediaWidget = (props) => {
    // Needed to deal with offset on dragged items
    // See https://github.com/atlassian/react-beautiful-dnd/issues/128
    const renderDraggable = (0, feed_factory_helpers_1.useDraggableInPortal)();
    const { t } = (0, react_i18next_1.useTranslation)();
    const onRemoveHandler = (id) => {
        const updatedMediaArray = props.mediaArray.filter((item) => {
            return item.hlink !== id;
        });
        props.onChange(updatedMediaArray);
    };
    const onChangeHandler = (id, key, value) => {
        const updatedMediaArray = props.mediaArray.map((item) => {
            if (item.hlink !== id) {
                return item;
            }
            // If field left empty, should return null instead of empty string
            if (value === "") {
                value = null;
            }
            if (key === "copyright") {
                return Object.assign(Object.assign({}, item), { [key]: value });
            }
            else if (key === "title.label") {
                return Object.assign(Object.assign({}, item), { title: { label: value || "" } });
            }
            else {
                console.error("Unknown field");
                return Object.assign({}, item);
            }
        });
        props.onChange(updatedMediaArray);
    };
    const onAddHandler = (type, urls) => {
        const newItems = [];
        if (typeof urls === "string") {
            urls = [urls];
        }
        // console.log(type, urls); // DEBUG
        switch (type) {
            case "youtube":
                urls.forEach((url) => {
                    // filename is video id here
                    const filename = youtubeParser(url);
                    if (filename) {
                        newItems.push({
                            filename: filename,
                            url: `https://www.youtube.com/watch?v=${filename}`,
                            type: type,
                        });
                    }
                    else {
                        alert(`${t("form.urlIncorrect")}: "${url}"`);
                    }
                });
                break;
            case "vimeo":
                urls.forEach((url) => {
                    const filename = vimeoParser(url);
                    if (filename) {
                        newItems.push({
                            filename: filename,
                            url: `https://vimeo.com/${filename}`,
                            type: type,
                        });
                    }
                    else {
                        alert(`${t("form.urlIncorrect")}: "${url}"`);
                    }
                });
                break;
            case "jpeg":
            case "png":
            case "jpegOrPng":
                urls.forEach((url) => {
                    const filename = imageParser(url);
                    if (filename) {
                        // Check file extension (jpeg or png)
                        const splitUrl = url.split(".");
                        newItems.push({
                            filename: filename,
                            url: url,
                            type: splitUrl[splitUrl.length - 1].toLowerCase(),
                        });
                    }
                    else {
                        alert(`${t("form.urlIncorrect")}: "${url}"`);
                    }
                });
                break;
            default:
                alert(`${t("form.unsupportedFileType")} ${type}`);
                return;
        }
        // console.log(newItems); // DEBUG
        const updatedMediaArray = Array.from(props.mediaArray);
        const newItemsTransformed = newItems.map((item) => {
            return {
                copyright: null,
                filename: item.filename,
                filetype: item.type,
                hlink: item.url,
                main: null,
                mediatype: type === "youtube" || type === "vimeo" ? "video" : "photo",
                targetLanguage: null,
                title: null,
                trcid: null,
            };
        });
        // console.log(props.mediaArray, updatedMediaArray, newItemsTransformed); // DEBUG
        props.onChange(updatedMediaArray.concat(newItemsTransformed));
    };
    const onDragEnd = (result) => {
        // dropped outside the list
        if (!result.destination) {
            return;
        }
        const updatedMediaArray = reorder(props.mediaArray, result.source.index, result.destination.index);
        props.onChange(updatedMediaArray);
    };
    // TODO: replace key with id once available
    return (react_1.default.createElement(react_beautiful_dnd_1.DragDropContext, { onDragEnd: onDragEnd },
        react_1.default.createElement(react_beautiful_dnd_1.Droppable, { droppableId: "media-widget", direction: "horizontal" }, (provided, snapshot) => (react_1.default.createElement("div", Object.assign({ className: MediaWidget_module_scss_1.default.MediaWidget, ref: provided.innerRef, style: getListStyle(snapshot.isDraggingOver) }, provided.droppableProps),
            props.mediaArray.map((mediaItem, index) => (react_1.default.createElement(react_beautiful_dnd_1.Draggable, { key: mediaItem.hlink, draggableId: mediaItem.hlink, index: index }, renderDraggable((provided, snapshot) => (react_1.default.createElement(ItemContainer_1.default, { ref: provided.innerRef, draggableProps: provided.draggableProps, dragHandleProps: provided.dragHandleProps, style: getItemStyle(snapshot.isDragging, provided.draggableProps.style), onRemove: onRemoveHandler, key: mediaItem.hlink, item: mediaItem, isDragging: snapshot.isDragging, handleItemChange: onChangeHandler },
                react_1.default.createElement(feed_factory_media_preview_1.default, { mediaItem: mediaItem }))))))),
            react_1.default.createElement(AddMediaPane_1.default, { onAddMediaHandler: onAddHandler, hidden: snapshot.isDraggingOver }),
            provided.placeholder)))));
};
exports.default = MediaWidget;
//# sourceMappingURL=MediaWidget.js.map